'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import Image from 'next/image';
import { useQuery } from '@apollo/client';
import { GET_INSTA } from '@/graphql/queries';

import 'swiper/css';
import 'swiper/css/free-mode';
import styles from './InstagramGallery.module.css';
import { AWS_URL } from '@/constants';

const INSTA_URL = 'https://www.instagram.com/isilverandgem';

// Instagram SVG icon — official gradient version
const InstagramIcon = () => (
  <svg
    viewBox="0 0 48 48"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: '36px', height: '36px', display: 'block' }}
  >
    <defs>
      <radialGradient id="ig-grad" cx="30%" cy="107%" r="130%">
        <stop offset="0%"  stopColor="#fdf497" />
        <stop offset="10%" stopColor="#fdf497" />
        <stop offset="50%" stopColor="#fd5949" />
        <stop offset="68%" stopColor="#d6249f" />
        <stop offset="100%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect width="48" height="48" rx="12" fill="url(#ig-grad)" />
    <rect x="14" y="14" width="20" height="20" rx="6" fill="none" stroke="#fff" strokeWidth="2.5" />
    <circle cx="24" cy="24" r="5.5" fill="none" stroke="#fff" strokeWidth="2.5" />
    <circle cx="34.5" cy="13.5" r="1.8" fill="#fff" />
  </svg>
);

export default function InstagramGallery() {
  const { data, loading, error } = useQuery(GET_INSTA, {
    variables: {
      where: {
        type: { _eq: "INSTAGRAM" },
        is_deleted: { _eq: false }
      }
    },
    fetchPolicy: 'network-only',
  });

  const getImageUrl = (fileData) => {
    if (!fileData) return '';
    if (fileData.path && !fileData.media) return `${AWS_URL}${fileData.path}`;
    if (Array.isArray(fileData) && fileData.length) {
      const path = fileData[0]?.media?.path || fileData[0]?.path;
      if (path) return `${AWS_URL}${path}`;
    }
    if (fileData?.media?.path) return `${AWS_URL}${fileData.media.path}`;
    return '';
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerSkeletonLine} />
        </div>
        <div className={styles.skeleton}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={styles.skeletonCard} style={{ animationDelay: `${i * 0.1}s` }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.message}>Error loading gallery.</p>
      </div>
    );
  }

  const posts = data?.tenant_org_web_configuration
    ?.flatMap((config) => config.web_configuration_meta)
    .filter((meta) => meta.file) ?? [];

  if (!posts.length) return null;

  return (
    <div className={styles.container}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerRule} />
          <span className={styles.headerEyebrow}>Follow us on</span>
          <div className={styles.headerRule} />
        </div>

        <a
          href={INSTA_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.headerBrand}
          aria-label="Visit @isilverandgem on Instagram"
        >
          <span className={styles.headerIconWrap}>
            <InstagramIcon />
          </span>
          <span className={styles.headerHandle}>@isilverandgem</span>
          <span className={styles.headerArrow}>↗</span>
        </a>
      </div>

      {/* ── Swiper Gallery ── */}
      <Swiper
        modules={[FreeMode]}
        spaceBetween={10}
        slidesPerView="auto"
        freeMode
        grabCursor
        className={styles.swiper}
      >
        {posts.map((post, index) => (
          <SwiperSlide key={index} style={{ width: 'auto' }} className={styles.slide}>
            <a
              href={post.redirection_url || INSTA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.card}
              aria-label={`Instagram post ${index + 1}`}
            >
              {/* Image */}
              <Image
                src={getImageUrl(post.file)}
                alt={`Instagram post ${index + 1}`}
                fill
                sizes="(max-width: 480px) 180px, (max-width: 768px) 220px, 280px"
                className={styles.image}
              />

              {/* Hover overlay */}
              <div className={styles.overlay}>
                <div className={styles.overlayInner}>
                  <div className={styles.overlayIcon}>
                    <InstagramIcon />
                  </div>
                  <span className={styles.overlayText}>View on Instagram</span>
                </div>
              </div>
            </a>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}